'use client'

/**
 * AI Chat Input (Issue #4429, #4443, #4435)
 *
 * Message input with Enter to send, Shift+Enter for new lines.
 * Resizable textarea with refined visual design.
 */

import { useState, useRef, useCallback, useEffect } from 'react'
import { Send, Loader2 } from '@/components/icons'
import { Textarea } from '@/components/ui/textarea'
import { clipboardImages } from '@/hooks/use-page-file-intake'
import type { SentChatAttachment } from '@/lib/ai-chat/chat-attachments'
import { AI_CHAT_FOCUS_INPUT_EVENT } from '@/lib/ai-chat/page-suggestions'
import { cn } from '@/lib/utils'
// The server's own limit, not a copy of it: this clamp and the route's 400
// have to be the same number or the input permits a message the API rejects.
// `client-text-budget` imports only types, so it is safe from a client
// component (#13431).
import { MAX_MESSAGE_LENGTH } from '@/lib/ai-chat/client-text-budget'
import { AI_ASK_CONTROL_CLASSES } from './ai-ask-control-styles'
import { ChatAttachButton, ChatAttachmentChips } from './chat-attachment-chips'
import type { ChatAttachments } from './use-chat-attachments'

/** Show the character counter once the message passes 90% of the limit. */
const NEAR_LIMIT_RATIO = 0.9

interface AiChatInputProps {
  /**
   * `attachments` is present only when files rode the message (#16101):
   * the ready files, already taken off the composer.
   */
  onSend: (
    message: string,
    extras?: { attachments: SentChatAttachment[] },
  ) => void
  /** True while the AI is generating a response; gates send but never disables the textarea */
  isStreaming?: boolean
  autoFocus?: boolean
  /**
   * Files on the message (#16101, part of AI chat since #16724): the
   * shared `useChatAttachments` state the panel owns. Adds the attach button
   * (`ai-chat-attach`, 44×44, left of Send), the chip row above the textarea
   * and image paste; Send waits while a chip uploads. Absent: unchanged.
   */
  attachments?: ChatAttachments
}

export function AiChatInput({
  onSend,
  isStreaming = false,
  autoFocus = false,
  attachments,
}: AiChatInputProps) {
  const [value, setValue] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const isUploading = attachments?.isUploading === true

  // Auto-focus on mount when drawer opens
  useEffect(() => {
    if (autoFocus) {
      // Small delay to let the drawer animation start
      const timer = setTimeout(() => textareaRef.current?.focus(), 100)
      return () => clearTimeout(timer)
    }
  }, [autoFocus])

  // An "Ask about this…" press opens the panel onto the input (#16117). A
  // panel mounting fresh focuses through `autoFocus`; this covers a reopen.
  useEffect(() => {
    const handler = () => textareaRef.current?.focus()
    window.addEventListener(AI_CHAT_FOCUS_INPUT_EVENT, handler)
    return () => window.removeEventListener(AI_CHAT_FOCUS_INPUT_EVENT, handler)
  }, [])

  // Listen for suggestion chips from empty state
  useEffect(() => {
    const handler = (e: Event) => {
      const suggestion = (e as CustomEvent).detail
      if (typeof suggestion === 'string') {
        // Set the value directly on the textarea element
        // AND update React state, then resize
        const ta = textareaRef.current
        if (ta) {
          ta.value = suggestion
          ta.style.height = 'auto'
          ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`
          ta.focus()
        }
        setValue(suggestion)
      }
    }
    window.addEventListener('ai-chat:suggest', handler)
    return () => window.removeEventListener('ai-chat:suggest', handler)
  }, [])

  const handleSend = useCallback(() => {
    const trimmed = value.trim()
    if (!trimmed || isStreaming || isUploading) return
    const taken = attachments?.takeForSend() ?? []
    if (taken.length > 0) onSend(trimmed, { attachments: taken })
    else onSend(trimmed)
    setValue('')
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
  }, [value, isStreaming, isUploading, attachments, onSend])

  // An image-only clipboard stages a chip; a paste carrying text is text.
  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
      if (!attachments) return
      if (event.clipboardData.getData('text/plain') !== '') return
      const images = clipboardImages(event.clipboardData)
      if (images.length === 0) return
      event.preventDefault()
      attachments.addFiles(images, 'paste')
    },
    [attachments],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        if (e.shiftKey || e.altKey) {
          return
        }
        e.preventDefault()
        handleSend()
      }
    },
    [handleSend],
  )

  const handleInput = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value
      if (newValue.length <= MAX_MESSAGE_LENGTH) {
        setValue(newValue)
      }
      const textarea = e.target
      textarea.style.height = 'auto'
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`
    },
    [],
  )

  const hasContent = value.trim().length > 0
  const canSend = hasContent && !isStreaming && !isUploading
  // `handleInput` ignores any change past the limit, so without this counter
  // typing just stops with no explanation.
  const nearLimit = value.length > MAX_MESSAGE_LENGTH * NEAR_LIMIT_RATIO
  const atLimit = value.length >= MAX_MESSAGE_LENGTH

  return (
    <div className="bg-surface-panel px-6 py-3 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
      <div
        className={cn(
          'flex items-end gap-2 p-3',
          attachments && attachments.chips.length > 0 && 'flex-wrap',
          AI_ASK_CONTROL_CLASSES,
        )}
      >
        {attachments ? (
          <ChatAttachmentChips
            chips={attachments.chips}
            onRemove={attachments.remove}
            className="w-full basis-full"
          />
        ) : null}
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          onPaste={attachments ? handlePaste : undefined}
          placeholder="Ask about your data..."
          rows={1}
          className="min-h-16 flex-1 resize-none border-0 bg-transparent px-1 py-2 text-text-primary shadow-none focus:ring-0 focus-visible:border-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
          aria-label="Chat message"
        />
        {attachments ? (
          <ChatAttachButton
            attachments={attachments}
            testId="ai-chat-attach"
            atCapTitle="This message has as many files as it can carry"
          />
        ) : null}
        <button
          onClick={handleSend}
          disabled={!canSend}
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors',
            canSend
              ? 'bg-action-primary text-white-static hover:bg-action-primary-hover'
              : 'bg-fill-10 text-text-secondary',
          )}
          aria-label="Send message"
          type="button"
        >
          {isStreaming ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </button>
      </div>
      <div className="mt-1 flex justify-end px-3">
        {nearLimit && (
          <span
            aria-hidden="true"
            className="font-mono text-xs tabular-nums text-text-secondary"
          >
            {value.length}/{MAX_MESSAGE_LENGTH}
          </span>
        )}
      </div>
      {/* Always mounted so the live region exists before its text changes.
          Its text changes only at the two thresholds, so a screen reader
          hears two announcements rather than one per keystroke. */}
      <div aria-live="polite" className="sr-only">
        {atLimit
          ? `Character limit reached (${MAX_MESSAGE_LENGTH}).`
          : nearLimit
            ? `Approaching the ${MAX_MESSAGE_LENGTH} character limit.`
            : ''}
      </div>
      <p className="mt-2 text-center text-xs text-text-secondary">
        AI can make mistakes. Your organization&apos;s data stays in your
        account.
      </p>
    </div>
  )
}
